import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Paper, Text, Box, Badge, Group, Divider, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

interface SuggestionItem {
  id: string;
  label: string;
  scope?: string;
  type?: string;
}

interface Props {
  items: SuggestionItem[];
  allItems?: SuggestionItem[];
  command: (item: SuggestionItem) => void;
  onBrowseAll?: () => void;
  hasQuery?: boolean;
}

const TYPE_COLORS: Record<string, string> = {
  STRING: 'blue',
  NUMBER: 'green',
  BOOLEAN: 'orange',
};

export const MentionList = forwardRef<any, Props>(({ items, allItems, command, onBrowseAll, hasQuery }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isBrowsingAll, setIsBrowsingAll] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const displayItems = useMemo(() => {
    const source = isBrowsingAll ? (allItems || items) : items;
    const query = search.trim().toLowerCase();
    if (!isBrowsingAll || !query) return source;

    return source.filter((item) =>
      [item.label, item.id, item.scope, item.type]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [allItems, isBrowsingAll, items, search]);
  const showBrowseAll = !isBrowsingAll && (allItems?.length || 0) > 0;
  const totalItems = displayItems.length + (showBrowseAll ? 1 : 0);

  useEffect(() => {
    setSelectedIndex(0);
    setIsBrowsingAll(false);
    setSearch('');
  }, [items]);

  useEffect(() => {
    if (isBrowsingAll) {
      window.requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isBrowsingAll]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const openAllVariables = () => {
    setIsBrowsingAll(true);
    setSelectedIndex(0);
    setSearch('');
    onBrowseAll?.();
  };

  const selectCurrentItem = () => {
    if (displayItems[selectedIndex]) {
      command(displayItems[selectedIndex]);
    }
  };

  const moveSelection = (direction: 1 | -1) => {
    if (totalItems <= 0) return;
    setSelectedIndex((prev) => (prev + direction + totalItems) % totalItems);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        moveSelection(-1);
        return true;
      }
      if (event.key === 'ArrowDown') {
        moveSelection(1);
        return true;
      }
      if (event.key === 'Enter') {
        if (showBrowseAll && selectedIndex === displayItems.length) {
          openAllVariables();
        } else {
          selectCurrentItem();
        }
        return true;
      }
      return false;
    },
  }));

  return (
    <Paper shadow="md" radius="sm" withBorder style={{ minWidth: 280, overflow: 'hidden' }}>
      {isBrowsingAll && (
        <Box p="xs" pb={6} style={{ borderBottom: '1px solid #E4E7ED' }}>
          <TextInput
            ref={searchRef}
            size="xs"
            leftSection={<IconSearch size={13} />}
            placeholder="Search variables"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveSelection(-1);
              }
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveSelection(1);
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                selectCurrentItem();
              }
              event.stopPropagation();
            }}
          />
        </Box>
      )}
      {displayItems.length > 0 && (
        <>
          <Box px="sm" py={4} style={{ backgroundColor: '#f8f9fa' }}>
            <Text size="xs" c="dimmed" fw={500}>
              {isBrowsingAll ? 'All variables' : hasQuery ? 'Variables' : 'Recently used'}
            </Text>
          </Box>
          {displayItems.map((item, index) => (
            <Box
              key={item.id}
              px="sm"
              py={6}
              onClick={() => command(item)}
              style={{
                cursor: 'pointer',
                backgroundColor: index === selectedIndex ? '#e8edf5' : 'transparent',
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <Group gap={6}>
                {item.type && (
                  <Badge size="xs" color={TYPE_COLORS[item.type] || 'gray'} variant="dot">
                    {item.type[0]}
                  </Badge>
                )}
                <Text size="xs" fw={500}>{item.label}</Text>
                {item.scope && (
                  <Text size="xs" c="dimmed">({item.scope.toLowerCase()})</Text>
                )}
              </Group>
            </Box>
          ))}
        </>
      )}
      {displayItems.length === 0 && (hasQuery || isBrowsingAll) && (
        <Box px="sm" py={6}>
          <Text size="xs" c="dimmed">No matching variables</Text>
        </Box>
      )}
      {showBrowseAll && (
        <>
          <Divider />
          <Box
            px="sm"
            py={6}
            onClick={openAllVariables}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedIndex === displayItems.length ? '#e8edf5' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
            onMouseEnter={() => setSelectedIndex(displayItems.length)}
          >
            <IconSearch size={13} style={{ color: '#415A91', flexShrink: 0 }} />
            <Text size="xs" fw={500} c="#415A91">Browse all variables…</Text>
          </Box>
        </>
      )}
    </Paper>
  );
});

MentionList.displayName = 'MentionList';
